import React, { useState } from 'react';
import { Row, Col, Table, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { useConfirm } from '../../shared/ConfirmContext';
import { IconPlus } from '@tabler/icons-react';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';

const TaxSettings = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    tax_type: 'VAT',
    rate: '',
    is_active: true,
    description: ''
  });

  const TAX_TYPE_CHOICES = [
    { value: 'VAT', label: 'VAT' },
    { value: 'GST', label: 'GST' },
    { value: 'SALES_TAX', label: 'Sales Tax' },
    { value: 'OTHER', label: t('settings.tax.typeOther') }
  ];

  const { data: taxes = [], isLoading } = useQuery({
    queryKey: ['tax-settings'],
    queryFn: () => api.get('settings/api/tax/').then(d => Array.isArray(d) ? d : []),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => editingTax
      ? api.put(`settings/api/tax/${editingTax.id}/`, payload)
      : api.post('settings/api/tax/', payload),
    onSuccess: () => {
      setSuccess(editingTax ? t('settings.tax.success.updated') : t('settings.tax.success.created'));
      setShowModal(false);
      queryClient.invalidateQueries({ queryKey: ['tax-settings'] });
    },
    onError: (err) => setError(err.message),
  });

  const defaultMutation = useMutation({
    mutationFn: (taxId) => api.post(`settings/api/tax/${taxId}/set_default/`),
    onSuccess: () => {
      setSuccess(t('settings.tax.success.defaultSet'));
      queryClient.invalidateQueries({ queryKey: ['tax-settings'] });
    },
    onError: (err) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (taxId) => api.delete(`settings/api/tax/${taxId}/`),
    onSuccess: () => {
      setSuccess(t('settings.tax.success.deleted'));
      queryClient.invalidateQueries({ queryKey: ['tax-settings'] });
    },
    onError: (err) => setError(err.message),
  });

  const loading = isLoading || saveMutation.isPending || defaultMutation.isPending || deleteMutation.isPending;

  const handleAdd = () => {
    setEditingTax(null);
    setFormData({ name: '', tax_type: 'VAT', rate: '', is_active: true, description: '' });
    setShowModal(true);
  };

  const handleEdit = (tax) => {
    setEditingTax(tax);
    setFormData({
      name: tax.name,
      tax_type: tax.tax_type,
      rate: tax.rate,
      is_active: tax.is_active,
      description: tax.description || ''
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    saveMutation.mutate(formData);
  };

  const handleSetDefault = (taxId) => {
    setError(null);
    setSuccess(null);
    defaultMutation.mutate(taxId);
  };

  const handleDelete = async (taxId) => {
    if (!await confirm(t('settings.tax.deleteConfirm'))) return;
    setError(null);
    setSuccess(null);
    deleteMutation.mutate(taxId);
  };

  return (
    <PageShell
      pretitle={t('settings.title')}
      title={t('settings.tax.title')}
      actions={
        <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={handleAdd}>
          {t('settings.tax.add')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <PageSection
        title={t('settings.tax.title')}
        description={t('settings.tax.subtitle')}
        bodyClassName="p-0"
      >
                    {loading && taxes.length === 0 ? (
                      <div className="text-center p-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t('common.loading')}
                      </div>
                    ) : (
                      <Table responsive hover className="mb-0">
                        <thead>
                          <tr>
                            <th>{t('settings.tax.name')}</th>
                            <th style={{ width: '15%' }}>{t('settings.tax.type')}</th>
                            <th style={{ width: '10%' }}>{t('settings.tax.rate')}</th>
                            <th style={{ width: '10%' }}>{t('settings.currency.isActive')}</th>
                            <th style={{ width: '1%' }} className="text-end">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxes.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-4">
                                {t('settings.tax.noTaxes')}
                              </td>
                            </tr>
                          ) : taxes.map(tax => (
                            <tr key={tax.id}>
                              <td>
                                <strong>{tax.name}</strong>
                                {tax.is_default && (
                                  <span className="badge bg-primary-lt ms-2">
                                    {t('settings.currency.default')}
                                  </span>
                                )}
                                {tax.description && (
                                  <div className="text-muted small">{tax.description}</div>
                                )}
                              </td>
                              <td>{tax.tax_type}</td>
                              <td><strong>{tax.rate}%</strong></td>
                              <td>
                                {tax.is_active ? (
                                  <span className="badge bg-success-lt">{t('settings.currency.active')}</span>
                                ) : (
                                  <span className="badge bg-secondary-lt">{t('settings.currency.inactive')}</span>
                                )}
                              </td>
                              <td className="text-end">
                                <div className="btn-group">
                                  {!tax.is_default && (
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={() => handleSetDefault(tax.id)}
                                      disabled={loading}
                                    >
                                      {t('settings.currency.setAsDefault')}
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    onClick={() => handleEdit(tax)}
                                    disabled={loading}
                                    title={t('common.edit')}
                                  >
                                    <i className="ti ti-edit"></i>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(tax.id)}
                                    disabled={loading}
                                    title={t('common.delete')}
                                  >
                                    <i className="ti ti-trash"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
      </PageSection>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTax ? t('settings.tax.edit') : t('settings.tax.add')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.tax.name')} *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('settings.tax.namePlaceholder')}
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tax.type')} *</Form.Label>
                  <Form.Select
                    value={formData.tax_type}
                    onChange={(e) => setFormData({ ...formData, tax_type: e.target.value })}
                    required
                  >
                    {TAX_TYPE_CHOICES.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tax.rate')} (%) *</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    placeholder="21.00"
                    step="any"
                    min="0"
                    max="100"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t('settings.tax.description')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('settings.tax.descriptionPlaceholder')}
              />
            </Form.Group>

            <Form.Check
              type="switch"
              id="tax-is-active"
              label={t('settings.currency.isActive')}
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <AppButton variant="primary" type="submit" loading={loading} loadingText={t('common.saving')}>
              {t('common.save')}
            </AppButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default TaxSettings;
